import { useEffect } from 'react';
import { toast } from 'sonner';

import { useUiStore } from '@/shared/store/ui-store';
import { useViewerStore } from '@/shared/store/viewer-store';

/*
 * ГЛОБАЛЬНЫЕ КЛАВИШИ ВЬЮПОРТА.
 *
 * Вынесены из `app/App.tsx` целиком. Причина не в размере файла: пока
 * обработчик жил внутри корневого компонента, единственным способом проверить
 * его было ЧТЕНИЕ ИСХОДНИКА — так и работал пин `keyboard-declared.test.ts`,
 * разбирая App.tsx регулярным выражением. Такой сторож видит текст, а не
 * поведение: он не отличит `Delete`, который скрывает, от `Delete`, который
 * объясняет, почему не скрывает.
 *
 * Здесь же поведение проверяется нажатием (`useGlobalViewerKeys.test.tsx`).
 *
 * `Escape` в этом обработчике НЕТ намеренно: он принадлежит оси выделения
 * (`useSelectionAxisKeys`), которой нужен список сущностей бандла.
 */
export function useGlobalViewerKeys(): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const editable = (e.target as HTMLElement)?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || editable) return;

      // Theme toggle
      if (e.key === 't' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        useUiStore.getState().toggleTheme();
        return;
      }

      // Render modes
      if (e.key === '1') {
        useViewerStore.getState().setRenderMode('solid');
        return;
      }
      if (e.key === '2') {
        useViewerStore.getState().setRenderMode('wireframe');
        return;
      }
      if (e.key === '3') {
        useViewerStore.getState().setRenderMode('xray');
        return;
      }

      // Selection / visibility
      const v = useViewerStore.getState();
      /* `Escape` здесь БОЛЬШЕ НЕ РАЗБИРАЕТСЯ. Он принадлежит оси выделения
         (`useSelectionAxisKeys`), потому что подъёму нужен список сущностей
         бандла, а этот обработчик о модели не знает. Вторая ветка на ту же
         клавишу снимала бы выделение сразу после подъёма. */
      if ((e.key === 'h' || e.key === 'H') && !e.metaKey && !e.ctrlKey) {
        if (e.shiftKey) {
          v.showAll();
        } else if (v.selectedIds.length || v.selectedId) {
          v.hideSelected();
        }
        e.preventDefault();
        return;
      }
      if (e.key === 'i' || e.key === 'I') {
        if (v.selectedIds.length) {
          v.isolateSelected();
          e.preventDefault();
        }
        return;
      }
      /*
       * `Delete` БОЛЬШЕ НЕ СКРЫВАЕТ (§7.6, правило 2; §7.11 п.7).
       *
       * Клавиша разрушения, делающая что-то другое, — ловушка: человек жмёт
       * `Delete`, шкаф пропадает с экрана, и человек уверен, что удалил его.
       * Он не удалён, он скрыт — и найти его обратно можно только через
       * «Показать всё», о котором в этот момент никто не думает.
       *
       * Удалять эта клавиша тоже не будет. Удаление модуля в продукте стоит
       * ЗА подменю «Ещё» контекстного меню — намеренно, чтобы промах мышью не
       * стоил шкафа. Повесить то же самое на голую клавишу значит снять этот
       * же барьер с другой стороны.
       *
       * Поэтому — названный отказ вместо тихого бездействия: нажатие
       * объясняет, какая клавиша скрывает и где лежит удаление. Скрытие
       * осталось на `H`, как и было.
       */
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (v.selectedIds.length || v.selectedId) {
          e.preventDefault();
          toast.info('Delete ничего не удаляет и больше не скрывает', {
            description: 'Скрыть выбранное — H, вернуть всё — Shift+H. Удалить модуль — правый клик → «Ещё» → «Удалить».',
          });
        }
        return;
      }

      // Open / close all facades
      if ((e.key === 'o' || e.key === 'O') && !e.metaKey && !e.ctrlKey) {
        if (e.shiftKey) {
          v.closeAllPanels();
        } else {
          v.openAllPanels();
        }
        e.preventDefault();
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
}
